/**
 * What this spend would do, shown before it is asked for.
 *
 * `spendImpact` has existed and been tested since the domain module was
 * written; this is the screen that finally calls it. Two numbers matter: where
 * the gauge lands, and how much further away the family goal gets. The second
 * is the one that does the teaching, because it converts an abstract "we
 * shouldn't" into a concrete trade the child can evaluate themselves.
 */

import { spendImpact } from '../domain/envelope.js';
import { formatCents } from '../domain/money.js';
import './SpendImpact.css';

const PERIOD_NOUN = { weekly: 'week', biweekly: 'fortnight', monthly: 'month' };

function delaySentence(periods, goalName, cadence) {
  const noun = PERIOD_NOUN[cadence] ?? 'week';
  if (!periods || periods <= 0) return `${goalName} still arrives on schedule.`;
  return `Pushes ${goalName} out about ${periods} ${noun}${periods === 1 ? '' : 's'}.`;
}

export default function SpendImpact({ balanceCents, amountCents, goal, refillCents, cadence = 'weekly' }) {
  const impact = spendImpact({ balanceCents, amountCents, goal, refillCentsPerPeriod: refillCents });

  return (
    <div className="impact">
      <div className="impact__row">
        <span>In the tank now</span>
        <span>{formatCents(balanceCents)}</span>
      </div>
      <div className="impact__row">
        <span>This costs</span>
        {/* formatCents emits an ASCII hyphen for negatives and that is the
            app-wide convention; a typographic minus here would disagree with
            the "Left after" row directly below it. */}
        <span>{formatCents(-amountCents)}</span>
      </div>
      <div className={`impact__row impact__after${impact.isOverdrawn ? ' impact__after--overdrawn' : ''}`}>
        <span>Left after</span>
        <span>{formatCents(impact.balanceAfterCents)}</span>
      </div>

      {impact.isOverdrawn && (
        <p className="impact__note">
          That’s {formatCents(impact.borrowedCents)} more than the tank holds. It would come out
          of next {PERIOD_NOUN[cadence] ?? 'week'}’s fuel.
        </p>
      )}

      {goal && (
        <p className="impact__note">
          {delaySentence(impact.goalPeriodsDelay, goal.name, cadence)}
        </p>
      )}
    </div>
  );
}
